import { useEffect, useRef, useState } from "react";

/**
 * Detecta si se deployó una versión nueva del front, sin depender del cache del
 * servidor. Compara el bundle que la app tiene cargado ahora contra el que figura
 * en /asset-manifest.json (que cambia de hash en cada build). El manifest se pide
 * con cache-busting (?_=timestamp + no-store) así siempre trae el estado real del
 * server, aunque el index.html esté cacheado.
 *
 * Solo funciona en el build de producción (en dev el bundle es "bundle.js" sin
 * hash, así que no dispara nada).
 */

const MANIFEST_URL = (process.env.PUBLIC_URL || "") + "/asset-manifest.json";

// Extrae "main.<hash>.js" de una ruta cualquiera (o null si no matchea).
function mainBundleName(path: string | null | undefined): string | null {
  if (!path) return null;
  const m = String(path).match(/\/static\/js\/(main\.[^/?"']+\.js)/);
  return m ? m[1] : null;
}

// El bundle main que la app tiene cargado en este momento (del <script> del index).
function currentMainBundle(): string | null {
  const scripts = Array.from(document.getElementsByTagName("script"));
  for (const s of scripts) {
    const name = mainBundleName(s.getAttribute("src"));
    if (name) return name;
  }
  return null;
}

async function latestMainBundle(): Promise<string | null> {
  try {
    const res = await fetch(`${MANIFEST_URL}?_=${Date.now()}`, {
      cache: "no-store",
    });
    if (!res.ok) return null;
    const data = await res.json();
    return mainBundleName(data?.files?.["main.js"]);
  } catch {
    return null;
  }
}

export default function useVersionCheck(intervalMs = 5 * 60 * 1000): boolean {
  const [updateAvailable, setUpdateAvailable] = useState(false);
  const current = useRef<string | null>(currentMainBundle());

  useEffect(() => {
    // Sin bundle con hash (dev) no tiene sentido chequear.
    if (!current.current) return;

    let active = true;

    const check = async () => {
      const latest = await latestMainBundle();
      if (active && latest && latest !== current.current) {
        setUpdateAvailable(true);
      }
    };

    const id = window.setInterval(check, intervalMs);
    const onVisible = () => {
      if (document.visibilityState === "visible") check();
    };
    window.addEventListener("focus", check);
    document.addEventListener("visibilitychange", onVisible);
    // Un primer chequeo por si la app se cargó de un index.html cacheado viejo.
    check();

    return () => {
      active = false;
      window.clearInterval(id);
      window.removeEventListener("focus", check);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [intervalMs]);

  return updateAvailable;
}
